import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CODLogoMark, SkylineDivider, CODLogoSVG } from "./Logo";
import { useAuth } from "../../lib/auth";
import { Button } from "../ui/button";
import { LayoutDashboard, MessageSquare, ListChecks, FileText, Calendar, Mail, LogOut, Users, ShieldCheck, Handshake, BookOpen, Briefcase, Building2, GraduationCap, Landmark, HeartHandshake, Bell, Settings, Target, ClipboardList, TrendingUp, Instagram, Facebook, Brain } from "lucide-react";

const PARTICIPANT_NAV = [
  { to: "/app/home", label: "Home", icon: LayoutDashboard, tid: "nav-home" },
  { to: "/app/plan", label: "My Plan", icon: BookOpen, tid: "nav-plan" },
  { to: "/app/tasks", label: "Tasks", icon: ListChecks, tid: "nav-tasks" },
  { to: "/app/documents", label: "Documents", icon: FileText, tid: "nav-docs" },
  { to: "/app/appointments", label: "Appointments", icon: Calendar, tid: "nav-appts" },
  { to: "/app/messages", label: "Messages", icon: Mail, tid: "nav-msgs" },
];
const STAFF_NAV = [
  { to: "/staff/caseload", label: "Caseload", icon: Users, tid: "nav-caseload" },
  { to: "/staff/handoffs", label: "Handoffs", icon: Handshake, tid: "nav-handoffs" },
  { to: "/staff/referrals", label: "Referrals", icon: Mail, tid: "nav-staff-referrals" },
  { to: "/staff/job-orders", label: "Job Orders", icon: Briefcase, tid: "nav-staff-jobs" },
  { to: "/staff/bd", label: "Business Dev", icon: Target, tid: "nav-staff-bd" },
  { to: "/staff/intelligence", label: "Intelligence", icon: Brain, tid: "nav-staff-oic" },
  { to: "/staff/leads", label: "Leads", icon: Bell, tid: "nav-staff-leads" },
];
const ADMIN_NAV = [
  { to: "/admin", label: "Admin", icon: ShieldCheck, tid: "nav-admin" },
  { to: "/admin/apps", label: "COS App Center", icon: LayoutDashboard, tid: "nav-admin-apps" },
  ...STAFF_NAV,
];

const EMPLOYER_NAV = [
  { to: "/portal/employer", label: "Dashboard", icon: LayoutDashboard, tid: "nav-emp-home" },
  { to: "/portal/employer/profile", label: "Organization", icon: Building2, tid: "nav-emp-profile" },
  { to: "/portal/employer/job-orders", label: "Job Orders", icon: Briefcase, tid: "nav-emp-jobs" },
  { to: "/portal/employer/candidates", label: "Candidates", icon: Users, tid: "nav-emp-candidates" },
  { to: "/portal/employer/hiring-activity", label: "Hiring Activity", icon: TrendingUp, tid: "nav-emp-activity" },
  { to: "/portal/employer/documents", label: "Documents", icon: FileText, tid: "nav-emp-docs" },
  { to: "/portal/employer/messages", label: "Messages", icon: Mail, tid: "nav-emp-msgs" },
];
const PARTNER_NAV = [
  { to: "/portal/partner", label: "Dashboard", icon: LayoutDashboard, tid: "nav-partner-home" },
  { to: "/partner/referrals", label: "Referrals", icon: Handshake, tid: "nav-partner-referrals" },
  { to: "/portal/partner/programs", label: "Programs", icon: ClipboardList, tid: "nav-partner-programs" },
  { to: "/portal/partner/documents", label: "Documents", icon: FileText, tid: "nav-partner-docs" },
  { to: "/portal/partner/messages", label: "Messages", icon: Mail, tid: "nav-partner-msgs" },
];
const TRAINING_NAV = [
  { to: "/portal/training", label: "Dashboard", icon: LayoutDashboard, tid: "nav-train-home" },
  { to: "/portal/training/cohorts", label: "Cohorts", icon: GraduationCap, tid: "nav-train-cohorts" },
  { to: "/portal/training/documents", label: "Documents", icon: FileText, tid: "nav-train-docs" },
  { to: "/portal/training/messages", label: "Messages", icon: Mail, tid: "nav-train-msgs" },
];
const GOV_NAV = [
  { to: "/portal/government", label: "Dashboard", icon: LayoutDashboard, tid: "nav-gov-home" },
  { to: "/portal/government/contracts", label: "Contracts", icon: Landmark, tid: "nav-gov-contracts" },
  { to: "/portal/government/documents", label: "Documents", icon: FileText, tid: "nav-gov-docs" },
  { to: "/portal/government/messages", label: "Messages", icon: Mail, tid: "nav-gov-msgs" },
];
const FUNDING_NAV = [
  { to: "/portal/funding", label: "Dashboard", icon: LayoutDashboard, tid: "nav-fund-home" },
  { to: "/portal/funding/grants", label: "Grants", icon: Landmark, tid: "nav-fund-grants" },
  { to: "/portal/funding/documents", label: "Documents", icon: FileText, tid: "nav-fund-docs" },
  { to: "/portal/funding/messages", label: "Messages", icon: Mail, tid: "nav-fund-msgs" },
];
const VOLUNTEER_NAV = [
  { to: "/portal/volunteer", label: "Dashboard", icon: LayoutDashboard, tid: "nav-vol-home" },
  { to: "/portal/volunteer/opportunities", label: "Opportunities", icon: HeartHandshake, tid: "nav-vol-opps" },
  { to: "/portal/volunteer/messages", label: "Messages", icon: Mail, tid: "nav-vol-msgs" },
];

function navForRole(role) {
  switch (role) {
    case "participant": return PARTICIPANT_NAV;
    case "staff": return STAFF_NAV;
    case "admin":
    case "super_admin": return ADMIN_NAV;
    case "employer": return EMPLOYER_NAV;
    case "partner":
    case "community_partner": return PARTNER_NAV;
    case "training_provider": return TRAINING_NAV;
    case "government_agency": return GOV_NAV;
    case "funding_org": return FUNDING_NAV;
    case "volunteer": return VOLUNTEER_NAV;
    case "instructor": return TRAINING_NAV;
    default: return PARTICIPANT_NAV;
  }
}

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  if (!user) return null;
  const nav = navForRole(user.role);
  const hasMultiRole = (user.roles || []).length > 1;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="cod-stripe"/>
      {/* WHITE institutional header (IDHS-inspired) */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="header-logo-link" aria-label="Chicago Opportunity Desk — Home" className="flex items-center">
            <CODLogoMark size={52} onLight/>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {nav.map(n => {
              const Icon = n.icon;
              const active = loc.pathname.startsWith(n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={n.tid}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-medium transition-colors border-b-2 ${active ? "text-[var(--cod-navy)] border-[var(--cod-red)]" : "text-slate-700 border-transparent hover:text-[var(--cod-navy)] hover:border-slate-200"}`}>
                  <Icon size={16}/> {n.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            {user.role === "participant" && (
              <Button onClick={() => navigate("/app/navigator")} data-testid="header-talk-to-navigator"
                className="hidden sm:inline-flex bg-[var(--cod-red)] hover:bg-[#A40D26] text-white">
                <MessageSquare size={16} className="mr-2"/> Talk to Navigator
              </Button>
            )}
            <div className="hidden lg:block text-right">
              <div className="text-sm font-medium text-slate-900" data-testid="header-user-name">{user.full_name}</div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{user.role.replace("_"," ")}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="header-logout" className="text-slate-700">
              <LogOut size={16}/>
            </Button>
            {hasMultiRole && (
              <Button variant="outline" size="sm" onClick={()=>navigate("/portal")} data-testid="header-switch-workspace" className="hidden sm:inline-flex">
                Switch workspace
              </Button>
            )}
          </div>
        </div>
        <div className="md:hidden border-t border-slate-200 overflow-x-auto scrollbar-thin">
          <div className="flex gap-1 px-3 py-2">
            {nav.map(n => {
              const active = loc.pathname.startsWith(n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={`m-${n.tid}`}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium ${active ? "bg-[var(--cod-navy)] text-white" : "bg-slate-100 text-slate-700"}`}>
                  {n.label}
                </Link>
              );
            })}
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">{children}</main>
      {/* Bright skyline-accented footer */}
      <footer className="bg-white border-t border-slate-200 mt-12">
        <SkylineDivider color="var(--cod-navy)" className="opacity-90"/>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-3 gap-8">
          <div>
            <CODLogoSVG size={64}/>
            <div className="mt-3 font-serif text-base font-semibold text-slate-900">Chicago Opportunity Desk</div>
            <p className="mt-3 text-sm text-slate-600 max-w-xs">A trusted Chicago institution helping residents access employment, education, certifications, and stability.</p>
          </div>
          <div className="text-sm">
            <div className="text-slate-900 font-semibold mb-3">Pathways</div>
            <ul className="space-y-1 text-slate-600">
              <li>Healthcare · CNA</li><li>Construction Trades</li>
              <li>Class A CDL</li><li>IT Support · CompTIA A+</li>
              <li>Record Relief · Housing</li>
            </ul>
          </div>
          <div className="text-sm">
            <div className="text-slate-900 font-semibold mb-3">Contact</div>
            <div className="text-slate-600">Chicago, IL · (312) 343-4183</div>
            <div className="text-slate-600"><a href="mailto:info@joinCOD.org" className="hover:underline">info@joinCOD.org</a></div>
            <div className="text-slate-600">Mon–Fri 9am–6pm Central</div>
            <div className="mt-3 flex items-center gap-2" data-testid="footer-social">
              <a href="https://www.instagram.com/join.cod" target="_blank" rel="noopener noreferrer" aria-label="COD on Instagram" data-testid="social-instagram"
                 className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-slate-200 text-slate-600 hover:text-white hover:bg-[var(--cod-navy)] hover:border-[var(--cod-navy)] transition-colors">
                <Instagram size={15}/>
              </a>
              <a href="https://www.facebook.com/profile.php?id=61591789641024" target="_blank" rel="noopener noreferrer" aria-label="COD on Facebook" data-testid="social-facebook"
                 className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-slate-200 text-slate-600 hover:text-white hover:bg-[var(--cod-navy)] hover:border-[var(--cod-navy)] transition-colors">
                <Facebook size={15}/>
              </a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-2 border-t border-slate-100 mt-2 text-xs text-slate-500">
          © Chicago Opportunity Desk · You are remembered. Something will move forward today.
        </div>
      </footer>
    </div>
  );
};
